import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import i18next from "i18next";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import BasicInput from "../../components/BasicInput";
import Button from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import FieldTitle from "../../components/FieldTitle";
import Label from "../../components/Label";
import SimpleDialog from "../../components/SimpleDialog";
import * as samplesServices from "../../services/samplesServices";
import { routes, utils } from "../../utils";
import AgeSelect from "../selects/AgeSelect";
import BasinSelect from "../selects/BasinSelect";
import ExtractionSelect from "../selects/ExtractionSelect";
import FormationSelect from "../selects/FormationSelect";
import GenderSelect from "../selects/GenderSelect";

const buttonSize = "40px";
const muiButtonStyle = { height: "40px", maxWidth: buttonSize, minWidth: buttonSize };

export default function Sample() {
	const [sample, setSample] = React.useState<any>({
		date: null,
		preparation_numbers: [null],
		meters: null,
		extraction: null,
		age: null,
		basins: [],
		genders: [],
		formation: null,
		additional_info: null,
	});

	const [errors, setErrors] = React.useState<any>({});
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);

	const { id } = useParams();

	useEffect(() => {
		if (id) {
			samplesServices
				.getSample(id)
				.then((sample) => setSample(sample))
				.catch((error) => {});
		}
	}, [id]);

	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	const validate = () => {
		const errors: any = { preparation_numbers: [] };

		if (!sample.meters) {
			errors.meters = i18next.t("required-field");
		}

		if (!sample.extraction) {
			errors.extraction = i18next.t("required-field");
		}

		if (!sample.date) {
			errors.date = i18next.t("required-field");
		}

		if (!sample.basins || sample.basins.length === 0) {
			errors.basins = i18next.t("required-field");
		}

		if (!sample.age) {
			errors.age = i18next.t("required-field");
		}

		if (errors.preparation_numbers.length === 0) delete errors.preparation_numbers;

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handelCancel = () => {
		if (editing) {
			setEditing(false);
		} else {
			navigate(-1);
		}
	};

	const handleCreateEditSample = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return;
			}

			const sampleData = {
				...sample,
				preparation_numbers: sample.preparation_numbers.filter((pN) => pN),
			};

			if (sample) {
				if (sample.id) {
					await samplesServices.updateSample(sampleData);
				} else {
					await samplesServices.createSample(sampleData);
				}
			}
			toast.success(i18next.t(editing ? "sample-edit-success" : "sample-create-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const handleDelete = async () => {
		try {
			if (!sample) {
				return;
			}
			await samplesServices.deleteSample(sample.id);
			toast.success(i18next.t("sample-delete-success"));
			navigate(routes.SAMPLE_LIST);
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const showValue = !!id && !editing;

	return (
		<SimpleDialog open={true} onClose={goBack}>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("sample-delete-confirm")}
				open={deleteModal}
			/>
			<div className="d-flex justify-content-between">
				<Label
					isTitle
					text={
						id
							? editing
								? i18next.t("entity-edit", { entity: i18next.t("Sample") })
								: i18next.t("sample-details")
							: i18next.t("entity-create", { entity: i18next.t("Sample") })
					}
				/>
				{editing && (
					<div
						className="text-danger"
						data-testid="delete-button"
						style={{ cursor: "pointer" }}
						onClick={() => setDeleteModal(true)}
					>
						{i18next.t("delete")}
					</div>
				)}
			</div>
			<BasicInput
				label={i18next.t("date")}
				value={sample?.date}
				required={true}
				onChange={(e) => setSample({ ...sample, date: e.target.value })}
				className="mt-2"
				error={errors.date}
				type="date"
				datatestid="date-input-sample"
				showValue={showValue}
			/>
			<FieldTitle label={i18next.t("preparation-number")} />
			{(showValue ? [true] : sample.preparation_numbers).map((_, index) => (
				<div key={index} className="d-flex align-items-start">
					<BasicInput
						key={index}
						value={sample?.preparation_numbers[index]}
						required={true}
						onChange={(e) => {
							sample.preparation_numbers[index] = parseInt(e.target.value);
							setSample({ ...sample, preparation_numbers: sample.preparation_numbers });
						}}
						renderValue={() => sample?.preparation_numbers.map((v: any) => v).join(", ")}
						className="mt-2 me-2"
						error={errors.preparation_numbers?.[index]}
						type="number"
						datatestid="preparation-number-input"
						showValue={showValue}
					/>

					{!showValue && (
						<div className="d-flex mt-2">
							{sample?.preparation_numbers?.length > 1 && (
								<MuiButton
									onClick={() => {
										if (errors?.preparation_numbers?.length - 1 >= index) {
											errors.preparation_numbers.splice(index, 1);
											setErrors({ ...errors });
										}

										if (sample?.preparation_numbers?.length - 1 >= index) {
											sample.preparation_numbers.splice(index, 1);
											setSample({ ...sample, preparation_numbers: sample.preparation_numbers });
										}
									}}
									sx={{ ...muiButtonStyle, marginRight: 1 }}
									data-testid={"preparation-number-plus-button"}
								>
									<DeleteIcon sx={{ width: 20, height: 20 }} color="error" />
								</MuiButton>
							)}
							{index == sample.preparation_numbers.length - 1 && (
								<MuiButton
									onClick={() => {
										sample.preparation_numbers.push(null);
										setSample({ ...sample, preparation_numbers: sample.preparation_numbers });
									}}
									sx={muiButtonStyle}
									data-testid={"preparation-number-plus-button"}
								>
									<AddIcon />
								</MuiButton>
							)}
						</div>
					)}
				</div>
			))}
			<BasicInput
				label={i18next.t("meters")}
				value={sample?.meters}
				required={true}
				onChange={(e) => setSample({ ...sample, meters: parseFloat(e.target.value) })}
				className="mt-2"
				error={errors.meters}
				type="number"
				datatestid="meters-input"
				showValue={showValue}
			/>

			<ExtractionSelect
				required={true}
				showValue={showValue}
				value={sample.extraction}
				error={errors.extraction}
				onChange={(value) => setSample({ ...sample, extraction: value?.value })}
			/>

			<AgeSelect
				required={true}
				showValue={showValue}
				value={sample.age}
				error={errors.age}
				onChange={(value) => setSample({ ...sample, age: value?.value })}
			/>

			<BasinSelect
				multiple={true}
				required={true}
				showValue={showValue}
				value={sample.basins?.map((basin: any) => ({
					...basin,
					value: basin,
					id: basin.id,
					label: basin.name,
				}))}
				error={errors.basins}
				onChange={(value) => setSample({ ...sample, basins: value?.map(({ value }) => value) })}
			/>

			<GenderSelect
				showValue={showValue}
				error={errors.genders}
				value={sample?.genders?.map((gender) => ({
					...gender,
					value: gender,
					id: gender.id,
					label: gender.name,
				}))}
				multiple={true}
				onChange={(value) => setSample({ ...sample, genders: value?.map(({ value }) => value) })}
			/>

			<FormationSelect
				showValue={showValue}
				error={errors.formation}
				value={sample.formation}
				onChange={(value) => setSample({ ...sample, formation: value?.value })}
			/>

			<BasicInput
				label={i18next.t("reference")}
				value={sample.additional_info}
				onChange={(e) => setSample({ ...sample, additional_info: e.target.value })}
				datatestid="additional-info-input"
				className="mt-3"
				showValue={showValue}
			/>
			<div className="d-flex mt-4 justify-content-end">
				<Button label={i18next.t("cancel")} onClick={handelCancel} className="ms-4" secondary />
				{id ? (
					editing ? (
						<Button
							label={i18next.t("save")}
							datatestid="save-button"
							onClick={handleCreateEditSample}
							className="ms-4"
						/>
					) : (
						<Button
							label={i18next.t("entity-edit", { entity: i18next.t("Sample") })}
							datatestid="edit-button"
							onClick={() => setEditing(true)}
							className="ms-4"
							visible={utils.isAdminOrCollaborator()}
						/>
					)
				) : (
					<Button
						label={i18next.t("entity-create", { entity: i18next.t("Sample") })}
						datatestid="create-button"
						onClick={handleCreateEditSample}
						className="ms-4"
					/>
				)}
			</div>
		</SimpleDialog>
	);
}
